<template>
  <div style="background-color: black; height: 100%">
    <!-- Back Button -->
    <v-btn icon dark class="back-button" @click="openBackConfirmation">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>

    <!-- Job Details -->
    <div v-if="job">
      <v-col cols="12" md="12" lg="10" xl="8" class="pa-2">
        <v-row class="fill-height pa-2 ma-0" justify="center">
          <h2 class="red--text font-weight-bold montserrat-title my-4">
            {{ job.jobName }} - Job #{{ job.jobNumber }}
          </h2>
        </v-row>
      </v-col>

      <div class="pa-6 add-job-card">
        <!-- Job Details Section -->
        <h3 class="section-title montserrat-title">Job Details</h3>
        <v-row dense class="no-gutters">
          <!-- Job Number -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Job Number"
              v-model="job.jobNumber"
              class="montserrat-input"
              :readonly="isInspection"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <!-- Seal Type -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Seal Type"
              v-model="job.jobName"
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <!-- Shaft Size -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Shaft Size"
              v-model="job.ShaftSize"
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <!-- Customer Name -->
          <v-col cols="12" md="3" class="pa-2">
            <v-autocomplete
              v-model="job.customerName"
              :items="customers"
              item-text="companyName"
              item-value="companyName"
              label="Customer Name"
              dense
              outlined
              prepend-inner-icon="mdi-account"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isSalesperson || isInspection"
            ></v-autocomplete>
          </v-col>

          <!-- Contact Person -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Contact Person"
              v-model="job.customerNumber"
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <!-- Engineer -->
          <v-col cols="12" md="3" class="pa-2">
            <v-select
              v-model="job.engineer"
              :items="engineers"
              label="Engineer"
              item-text="fullName"
              item-value="docId"
              outlined
              dense
              prepend-inner-icon="mdi-account"
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-select>
          </v-col>

          <!-- Technician -->
          <v-col cols="12" md="3" class="pa-2">
            <v-select
              v-model="job.technician"
              :items="technicians"
              label="Technician"
              item-text="fullName"
              item-value="docId"
              outlined
              dense
              prepend-inner-icon="mdi-account"
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-select>
          </v-col>

          <!-- Salesperson -->
          <v-col cols="12" md="3" class="pa-2">
            <v-select
              v-model="job.salesperson"
              :items="salespersons"
              label="Salesperson"
              item-text="fullName"
              item-value="docId"
              outlined
              dense
              prepend-inner-icon="mdi-account"
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-select>
          </v-col>

          <!-- Urgency -->
          <v-col cols="12" md="3" class="pa-2">
            <v-select
              v-model="job.urgency"
              :items="urgencyOptions"
              label="Priority"
              outlined
              dense
              class="montserrat-input"
              :readonly="isInspection"
              :rules="[rules.required]"
            ></v-select>
          </v-col>

          <!-- Inspection Date -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Inspection Date"
              v-model="job.currentDate"
              class="montserrat-input"
              :readonly="isInspection"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <!-- Total Lead Time -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Total Lead Time"
              v-model="job.TotalLead"
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <!-- Status -->
          <v-col cols="12" md="3" class="pa-2">
            <v-select
              dense
              outlined
              label="Status"
              v-model="job.status"
              :items="statusOptions"
              :rules="[rules.required]"
              class="montserrat-input"
              @change="handleStatusChange"
            ></v-select>
          </v-col>

          <!-- Conditional Fields -->
          <v-col cols="12" md="3" class="pa-2" v-if="showQuoteNumberField">
            <v-text-field
              dense
              outlined
              label="Quote Number"
              v-model="job.quoteNumber"
              class="montserrat-input"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3" class="pa-2" v-if="showSalesOrderField">
            <v-text-field
              dense
              outlined
              label="Sales Order Number"
              v-model="job.salesOrderNumber"
              class="montserrat-input"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3" class="pa-2" v-if="showInvoiceNumberField">
            <v-text-field
              dense
              outlined
              label="Invoice Number"
              v-model="job.invoiceNumber"
              class="montserrat-input"
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="3" class="pa-2" v-if="showReasonField">
            <v-text-field
              dense
              outlined
              label="Reason for Cancellation"
              v-model="job.cancellationReason"
              class="montserrat-input"
              :rules="[rules.required]"
              :readonly="isSalesperson || isInspection"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Job Description -->
        <v-col cols="12" md="12" class="pa-2">
          <v-textarea
            v-model="job.jobDescription"
            label="Job Description"
            outlined
            dense
            prepend-inner-icon="mdi-format-list-bulleted"
            class="montserrat-input"
            :rules="[rules.required]"
            style="height: 100%"
            :readonly="isInspection"
          ></v-textarea>
        </v-col>

        <!-- Photo Download Button & Carousel Dialog -->
        <v-col cols="12" md="12" class="pa-2">
          <v-row justify="end">
            <v-btn dark color="blue" dense @click="openPhotoDialog">
              Download All Photos
            </v-btn>
          </v-row>
        </v-col>

        <v-dialog v-model="photoDialog" max-width="800px">
          <v-card>
            <v-card-title class="headline">Job Photos</v-card-title>
            <v-card-text>
              <v-carousel v-if="!loadingPhotos && jobPhotos.length > 0" hide-delimiters>
                <v-carousel-item v-for="(photo, index) in jobPhotos" :key="index">
                  <v-img :src="photo" class="carousel-photo" max-height="400px"></v-img>
                </v-carousel-item>
              </v-carousel>
              <div v-if="loadingPhotos" class="loading-photos">
                <v-progress-circular indeterminate color="blue"></v-progress-circular>
                <span>Loading photos...</span>
              </div>
              <div v-if="!loadingPhotos && jobPhotos.length === 0">
                <p>No photos available for this job.</p>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="red" dark @click="photoDialog = false">Close</v-btn>
              <v-btn icon @click="downloadPhotos" :disabled="isSalesperson || isInspection">
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Hidden File Input for Photo Reupload -->
        <input
          type="file"
          ref="fileInput"
          style="display: none"
          @change="handlePhotoReupload"
        />

        <!-- Checklist Button -->
        <v-row dense justify="start">
          <v-col class="pa-2">
            <v-btn dark color="blue" dense @click="openChecklistDialog" class="montserrat-button">
              View Checklist
            </v-btn>
          </v-col>
        </v-row>

        <!-- Download All Photos Button -->
        <v-row justify="end">
          <v-btn dark color="blue" dense @click="openAndDownloadPhotos" class="ma-2 montserrat-button">
            <v-icon left>mdi-download</v-icon>Download All Photos
          </v-btn>
        </v-row>

        <!-- Subtotals Section -->
        <v-row justify="start">
          <v-col cols="12" md="3" class="pa-2">
            <h3 class="section-title montserrat-title">SUB_TOTALS</h3>
          </v-col>
        </v-row>

        <v-row justify="start">
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Labour Subtotal"
              v-model="job.labourSubTotal"
              readonly
              class="montserrat-input"
            ></v-text-field>

            <v-text-field
              dense
              outlined
              label="Stock/Parts Subtotal"
              v-model="job.stockSubTotal"
              readonly
              class="montserrat-input"
            ></v-text-field>

            <v-text-field
              dense
              outlined
              label="Grand Total"
              v-model="job.grandTotal"
              readonly
              class="montserrat-input"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Additional Information Section -->
        <h3 class="section-title montserrat-title">Additional Information</h3>
        <v-row dense class="no-gutters">
          <!-- Sample Number -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Sample Number"
              v-model="job.sampleNumber"
              class="montserrat-input"
              :readonly="isInspection"
            ></v-text-field>
          </v-col>

          <!-- Customer Reference -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Customer Reference"
              v-model="job.customerReference"
              class="montserrat-input"
              :readonly="isInspection"
            ></v-text-field>
          </v-col>

          <!-- Date Quoted -->
          <v-col cols="12" md="3" class="pa-2">
            <v-text-field
              dense
              outlined
              label="Date Quoted"
              v-model="job.dateQuoted"
              class="montserrat-input"
              readonly
              :rules="[rules.required]"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Custom Fields Section (Journal Field) -->
        <h3 class="section-title montserrat-title">Journal Field</h3>
        <v-row>
          <v-col cols="12" md="3" v-for="(customField, index) in job.customFields" :key="index" class="item-group pa-2">
            <v-text-field
              v-model="customField.value"
              label="Journal Description"
              outlined
              dense
              class="montserrat-input"
              :readonly="isSalesperson || isInspection"
              :rules="[rules.required]"
            ></v-text-field>
            <v-btn dark color="red" dense @click="confirmRemoveCustomField(index)" class="montserrat-button my-2" :disabled="isSalesperson || isInspection">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Add Custom Field Button -->
        <v-row justify="left" class="pa-2">
          <v-btn dark color="blue" @click="confirmAction('addCustomField')" class="montserrat-button my-8" :disabled="isSalesperson || isInspection">
            <v-icon>mdi-plus</v-icon>Add Journal Number
          </v-btn>
        </v-row>

        <!-- Summary Section -->
        <v-col cols="12" md="12" class="pa-2">
          <v-textarea
            v-model="job.JobSummary"
            label="Special Notes/Specific Requirements"
            outlined
            dense
            prepend-inner-icon="mdi-comment-text-outline"
            class="montserrat-input"
            style="height: 100%"
            :readonly="isInspection"
          ></v-textarea>
        </v-col>

        <!-- Save Button -->
        <v-col class="pa-2">
          <v-btn dark color="red" dense width="100%" @click="confirmAction('saveJobDetails')">
            Save
          </v-btn>
        </v-col>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else>
      <p>Loading...</p>
    </div>

    <!-- Checklist Component -->
    <display-checklist ref="checklist" :jobNumber="job.jobNumber" v-model="checklistDialog" />

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>{{ confirmationMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="executeConfirmedAction">Yes</v-btn>
          <v-btn color="red darken-1" text @click="confirmationDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Custom Field Confirmation Dialog -->
    <v-dialog v-model="dialogAddCustomField" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Addition</v-card-title>
        <v-card-text>Are you sure you want to add a new journal field?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dense dark @click="addCustomFieldConfirmed">Add</v-btn>
          <v-btn dense dark @click="dialogAddCustomField = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove Custom Field Confirmation Dialog -->
    <v-dialog v-model="dialogRemoveCustomField" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Removal</v-card-title>
        <v-card-text>Are you sure you want to remove this journal field?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" dense dark @click="removeCustomFieldConfirmed(confirmedIndex)">Remove</v-btn>
          <v-btn dense dark @click="dialogRemoveCustomField = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Back Confirmation Dialog -->
    <v-dialog v-model="backDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>
          Are you sure you want to go back? Unsaved changes might be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dark @click="goBack">Yes</v-btn>
          <v-btn color="grey" dark @click="backDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars for Success and Error Messages -->
    <v-snackbar v-model="successSnack.show" :timeout="3000" color="green" top right>
      {{ successSnack.message }}
      <v-btn text @click="successSnack.show = false">Close</v-btn>
    </v-snackbar>

    <v-snackbar v-model="errorSnack.show" :timeout="3000" color="red" top right>
      {{ errorSnack.message }}
      <v-btn text @click="errorSnack.show = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";
import DisplayChecklist from "@/components/DisplayChecklist.vue";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  components: {
    DisplayChecklist,
  },
  data() {
    return {
      photoDialog: false,
      selectedPhoto: null,
      job: null,
      jobPhotos: [],
      loadingPhotos: false,
      loading: true,
      checklistDialog: false,
      customers: [],
      suppliers: [],
      jobDescription: "",
      JobSummary: "",
      engineers: [],
      technicians: [],
      salespersons: [],
      dialogAddCustomField: false,
      dialogRemoveCustomField: false,
      confirmedIndex: null,
      leadTimeOptions: [
        "1-2 DAYS",
        "2-3 DAYS",
        "3-5 DAYS",
        "4-6 DAYS",
        "5-7 DAYS",
        "6-8 DAYS",
        "8-10 DAYS",
        "10-12 DAYS",
        "10-15 DAYS",
        "15-20 DAYS",
        "6-8 WEEKS",
        "8-10 WEEKS",
        "10-12 WEEKS",
        "Ex - Stock",
        "Custom",
      ],
      TotalLead: "",
      ShaftSize: "",
      urgencyOptions: ["Urgent", "Medium", "Not Urgent"],
      isSalesperson: false,
      isTechnician: false,
      isAdmin: false,
      originalStatus: null,
      statuses: [
        "Inspection",
        "Costing Complete",
        "Quoted",
        "InProgress",
        "Ready",
        "Completed",
        "Delayed",
        "Canceled",
      ],
      statusOrder: [
        "Inspection",
        "Costing Complete",
        "Quoted",
        "InProgress",
        "Ready",
        "Completed",
        "Delayed",
        "Canceled",
      ],
      rules: {
        required: (value) => !!value || "Required.",
        positive: (value) => value > 0 || "Must be positive.",
        nonNegative: (value) => value >= 0 || "Cannot be negative.",
      },
      confirmationDialog: false,
      confirmedAction: null,
      confirmationMessage: "",
      successSnack: {
        show: false,
        message: "",
      },
      errorSnack: {
        show: false,
        message: "",
      },
      hasChanges: false,
      backDialog: false,
      isTablet: false,
      // NEW: currentUser will hold the current user's document from Firestore
      currentUser: null,
    };
  },
  watch: {
    job: {
      handler(newVal) {
        if (newVal) {
          this.calculateGrandTotal();
          this.hasChanges = true;
        }
      },
      deep: true,
    },
  },
  async created() {
    await this.initialize();
  },
  mounted() {
    this.checkTabletMode();
    window.addEventListener("resize", this.checkTabletMode);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkTabletMode);
  },
  methods: {
    async initialize() {
      // First, fetch the current user's document
      await this.fetchCurrentUser();
      await this.fetchJobDetails();
      await this.fetchCustomers();
      await this.fetchSuppliers();
      await this.fetchEngineers();
      await this.fetchTechnicians();
      await this.fetchSalespersons();
      this.checkUserRole();
      this.calculateGrandTotal();
    },

    // Fetch the current user's document from Firestore using Firebase Auth
    async fetchCurrentUser() {
      const auth = getAuth(firebaseApp);
      const user = auth.currentUser;
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            this.currentUser = userDoc.data();
          }
        } catch (error) {
          console.error("Error fetching current user data:", error);
        }
      }
    },

    selectAllText(event) {
      event.target.select();
    },

    addQuote() {
      this.job.extraInfoFields.push({
        text: "",
        fileUrl: null,
      });
      this.hasChanges = true;
    },

    triggerQuoteFileUpload(index) {
      if (this.$refs.quoteFileInputs && this.$refs.quoteFileInputs[index]) {
        this.$refs.quoteFileInputs[index].click();
      } else {
        this.errorSnack.message = "File input not available.";
        this.errorSnack.show = true;
      }
    },

    async handleQuoteFileUpload(event, index) {
      const file = event.target.files[0];
      if (file) {
        try {
          const storageRef = ref(
            storage,
            `jobs/${this.job.jobNumber}/supplierQuotes/${Date.now()}_${file.name}`
          );
          await uploadBytes(storageRef, file);
          const fileUrl = await getDownloadURL(storageRef);
          this.$set(this.job.extraInfoFields[index], "fileUrl", fileUrl);
          this.successSnack.message = "File uploaded successfully.";
          this.successSnack.show = true;
          await this.saveJobDetails();
        } catch (error) {
          console.error("Error uploading file:", error);
          this.errorSnack.message = "Failed to upload file.";
          this.errorSnack.show = true;
        }
      }
    },

    viewFile(index) {
      const fileUrl = this.job.extraInfoFields[index].fileUrl;
      if (fileUrl) {
        this.selectedFileUrl = fileUrl;
        this.fileDialog = true;
      } else {
        console.error("No file URL found for viewing.");
        this.errorSnack.message = "No file available to view.";
        this.errorSnack.show = true;
      }
    },

    confirmDeleteField(index) {
      this.fieldToDeleteIndex = index;
      this.deleteDialog = true;
    },

    async deleteFieldConfirmed() {
      const index = this.fieldToDeleteIndex;
      const field = this.job.extraInfoFields[index];
      if (field && field.fileUrl) {
        try {
          const filePath = field.fileUrl.split("?")[0].split("/o/")[1].replace(/%2F/g, "/");
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
          console.log("File deleted successfully");
          this.successSnack.message = "File deleted successfully.";
          this.successSnack.show = true;
        } catch (error) {
          console.error("Error deleting file:", error);
          this.errorSnack.message = "Failed to delete file.";
          this.errorSnack.show = true;
        }
      }
      this.job.extraInfoFields.splice(index, 1);
      this.hasChanges = true;
      await this.saveJobDetails();
      this.deleteDialog = false;
      this.fieldToDeleteIndex = null;
    },

    openSupplierEditDialog(index) {
      const stockItem = this.job.stockItems[index];
      this.editSupplier = {
        index: index,
        supplier: stockItem.supplier,
        drawingNumber: stockItem.drawingNumber,
        specialNote: stockItem.specialNote,
      };
      this.supplierEditDialog = true;
    },

    async saveSupplierDetails() {
      if (this.$refs.supplierForm.validate()) {
        const { index, supplier, drawingNumber, specialNote } = this.editSupplier;
        const stockItem = this.job.stockItems[index];
        stockItem.supplier = supplier;
        stockItem.drawingNumber = drawingNumber;
        stockItem.specialNote = specialNote;
        this.hasChanges = true;
        await this.saveJobDetails();
        this.supplierEditDialog = false;
        this.successSnack.message = "Supplier details updated successfully.";
        this.successSnack.show = true;
      } else {
        this.errorSnack.message = "Please fill in all required fields.";
        this.errorSnack.show = true;
      }
    },

    async fetchJobDetails() {
      const jobId = localStorage.getItem("selectedJob");
      if (jobId) {
        try {
          this.loading = true;
          const jobRef = doc(db, "jobs", jobId);
          const jobDoc = await getDoc(jobRef);
          if (jobDoc.exists()) {
            const jobData = jobDoc.data();
            this.job = {
              jobNumber: jobData.jobNumber || "",
              jobName: jobData.jobName || "",
              customerName: jobData.customerName || "",
              customerNumber: jobData.customerNumber || "",
              urgency: jobData.urgency || "",
              timeToComplete: jobData.timeToComplete || "",
              setRate: jobData.setRate || "",
              currentDate: jobData.currentDate || "",
              dateQuoted: jobData.dateQuoted || "",
              engineer: jobData.engineer || "",
              technician: jobData.technician || "",
              salesperson: jobData.salesperson || "",
              status: jobData.status || "",
              quoteNumber: jobData.quoteNumber || "",
              TotalLead: jobData.TotalLead || "",
              ShaftSize: jobData.ShaftSize || "",
              salesOrderNumber: jobData.salesOrderNumber || "",
              invoiceNumber: jobData.invoiceNumber || "",
              cancellationReason: jobData.cancellationReason || "",
              photos: jobData.photos || [],
              jobDescription: jobData.jobDescription || "",
              JobSummary: jobData.JobSummary || "",
              labourItems: jobData.labourItems || [],
              stockItems: jobData.stockItems || [],
              customFields: jobData.customFields || [],
              labourSubTotal: jobData.labourSubTotal || 0,
              stockSubTotal: jobData.stockSubTotal || 0,
              grandTotal: jobData.grandTotal || 0,
              sampleNumber: jobData.sampleNumber || "",
              customerReference: jobData.customerReference || "",
              inventoryJournalNumber: jobData.inventoryJournalNumber || "",
              extraInfoFields: jobData.extraInfoFields || [],
              permissions: jobData.permissions || {},
            };

            this.jobPhotos = [...this.job.photos];
            this.originalStatus = this.job.status;
            console.log("Job data loaded successfully:", this.job);
          } else {
            console.error("No such job found");
            this.errorSnack.message = "No such job found.";
            this.errorSnack.show = true;
          }
        } catch (error) {
          console.error("Error fetching job details:", error);
          this.errorSnack.message = "Error fetching job details.";
          this.errorSnack.show = true;
        } finally {
          this.loading = false;
        }
      } else {
        console.error("No job ID found in local storage");
        this.errorSnack.message = "No job ID found.";
        this.errorSnack.show = true;
      }
    },

    async fetchCustomers() {
      try {
        const cachedCustomers = localStorage.getItem("customers");
        if (cachedCustomers) {
          this.customers = JSON.parse(cachedCustomers);
        } else {
          const customersCollection = collection(db, "customers");
          const customersSnapshot = await getDocs(customersCollection);
          this.customers = customersSnapshot.docs.map((doc) => ({
            docId: doc.id,
            companyName: doc.data()["Company Name"] || "",
          }));
          this.customers.sort((a, b) =>
            a.companyName.localeCompare(b.companyName)
          );
          localStorage.setItem("customers", JSON.stringify(this.customers));
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
        this.errorSnack.message = "Error fetching customers.";
        this.errorSnack.show = true;
      }
    },

    async fetchSuppliers() {
      try {
        const cachedSuppliers = localStorage.getItem("suppliers");
        if (cachedSuppliers) {
          this.suppliers = JSON.parse(cachedSuppliers);
        } else {
          const suppliersCollection = collection(db, "suppliers");
          const suppliersSnapshot = await getDocs(suppliersCollection);
          this.suppliers = suppliersSnapshot.docs.map((doc) => ({
            docId: doc.id,
            supplierName: doc.data()["Supplier Name"] || "",
          }));
          this.suppliers.sort((a, b) =>
            a.supplierName.localeCompare(b.supplierName)
          );
          localStorage.setItem("suppliers", JSON.stringify(this.suppliers));
        }
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        this.errorSnack.message = "Error fetching suppliers.";
        this.errorSnack.show = true;
      }
    },

    async fetchEngineers() {
      try {
        const cachedEngineers = localStorage.getItem("engineers");
        if (cachedEngineers) {
          this.engineers = JSON.parse(cachedEngineers);
        } else {
          const usersCollection = collection(db, "users");
          const usersSnapshot = await getDocs(usersCollection);
          this.engineers = usersSnapshot.docs
            .map((doc) => ({
              docId: doc.id,
              fullName: doc.data().fullName,
              role: doc.data().role,
            }))
            .filter((user) => user.role === "Engineer")
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
          localStorage.setItem("engineers", JSON.stringify(this.engineers));
        }
      } catch (error) {
        console.error("Error fetching engineers:", error);
        this.errorSnack.message = "Error fetching engineers.";
        this.errorSnack.show = true;
      }
    },

    async fetchTechnicians() {
      try {
        const cachedTechnicians = localStorage.getItem("technicians");
        if (cachedTechnicians) {
          this.technicians = JSON.parse(cachedTechnicians);
        } else {
          const usersCollection = collection(db, "users");
          const usersSnapshot = await getDocs(usersCollection);
          this.technicians = usersSnapshot.docs
            .map((doc) => ({
              docId: doc.id,
              fullName: doc.data().fullName,
              role: doc.data().role,
            }))
            .filter((user) => user.role === "Technician")
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
          localStorage.setItem("technicians", JSON.stringify(this.technicians));
        }
      } catch (error) {
        console.error("Error fetching technicians:", error);
        this.errorSnack.message = "Error fetching technicians.";
        this.errorSnack.show = true;
      }
    },

    async fetchSalespersons() {
      try {
        const cachedSalespersons = localStorage.getItem("salespersons");
        if (cachedSalespersons) {
          this.salespersons = JSON.parse(cachedSalespersons);
        } else {
          const usersCollection = collection(db, "users");
          const usersSnapshot = await getDocs(usersCollection);
          this.salespersons = usersSnapshot.docs
            .map((doc) => ({
              docId: doc.id,
              fullName: doc.data().fullName,
              role: doc.data().role,
            }))
            .filter((user) => user.role === "Salesperson")
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
          localStorage.setItem("salespersons", JSON.stringify(this.salespersons));
        }
      } catch (error) {
        console.error("Error fetching salespersons:", error);
        this.errorSnack.message = "Error fetching salespersons.";
        this.errorSnack.show = true;
      }
    },

    getLoggedInUserRole() {
      return localStorage.getItem("role") || "Admin";
    },

    checkUserRole() {
      const role = this.getLoggedInUserRole();
      this.isSalesperson = role === "Salesperson";
      this.isTechnician = role === "Technician";
      this.isAdmin = role === "Admin";
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async handlePhotoReupload(event) {
      const file = event.target.files[0];
      if (file) {
        const storageRef = ref(
          storage,
          `jobs/${this.job.jobNumber}/photos/${Date.now()}_${file.name}`
        );
        try {
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          if (this.job.photos.length > 0) {
            this.job.photos[0] = downloadURL;
          } else {
            this.job.photos.push(downloadURL);
          }
          this.hasChanges = true;
          this.notify("success", "Photo Uploaded", "The photo has been uploaded successfully.");
        } catch (error) {
          console.error("Error uploading photo:", error);
          this.notify("danger", "Upload Failed", "Failed to upload the photo.");
        }
      }
    },

    checkTabletMode() {
      this.isTablet = window.innerWidth <= 1024;
    },

    async fetchChecklistPhotos(jobNumber) {
      const checklistRef = collection(db, "checklists");
      const q = query(checklistRef, where("jobNumber", "==", jobNumber));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return [];
      const checklist = querySnapshot.docs[0].data().checklist || [];
      // Flatten all section.photos arrays
      return checklist.flatMap(section => section.photos || []);
    },
    async openPhotoDialog() {
      if (!this.job || !this.job.jobNumber) {
        this.notify("danger", "Job Number Missing", "No job number available.");
        return;
      }
      this.photoDialog = true;
      this.loadingPhotos = true;
      try {
        this.jobPhotos = await this.fetchChecklistPhotos(this.job.jobNumber);
        if (this.jobPhotos.length === 0) {
          this.notify("info", "No Photos", "No photos available for this job.");
        }
      } catch (error) {
        console.error("Error fetching checklist photos:", error);
        this.notify("danger", "Error", "Error fetching checklist photos.");
      } finally {
        this.loadingPhotos = false;
      }
    },
    async openAndDownloadPhotos() {
      try {
        await this.openPhotoDialog();
        if (this.jobPhotos.length > 0) {
          await this.downloadPhotos();
        } else {
          this.notify("info", "No Photos", "No photos available to download.");
        }
      } catch (error) {
        console.error("Error in opening or downloading photos:", error);
        this.notify("danger", "Download Failed", "Failed to download photos.");
      }
    },
    async downloadPhotos() {
      if (!this.jobPhotos.length) {
        this.notify("info", "No Photos", "No photos available to download.");
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(`job_${this.job.jobNumber}_photos`);
      const downloadPromises = this.jobPhotos.map((photoUrl, index) =>
        fetch(photoUrl)
          .then((response) => response.blob())
          .then((blob) => {
            const filename = photoUrl.split("/").pop().split("?")[0];
            folder.file(`photo_${index + 1}_${filename}`, blob);
          })
      );
      try {
        await Promise.all(downloadPromises);
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, `job_${this.job.jobNumber}_photos.zip`);
        this.notify("success", "Download Complete", "Photos have been downloaded as a ZIP file.");
      } catch (error) {
        console.error("Error downloading photos as ZIP:", error);
        this.notify("danger", "Download Failed", "Failed to download photos.");
      }
    },

    handleItemChange() {
      this.job.labourItems.forEach((item) => this.calculateLabourTotalPrice(item));
      this.job.stockItems.forEach((item) => this.calculateStockTotalPrice(item));
      this.calculateGrandTotal();
      this.hasChanges = true;
    },

    calculateLabourTotalPrice(item) {
      const quantity = +item.quantity || 0;
      const markUp = +item.markUp || 0;
      const unitPrice = +item.unitPrice || 0;
      const partSize = +item.partSize || 0;

      if (item.jobCategory === "Custom") {
        item.totalPrice = quantity * unitPrice;
      } else {
        item.unitPrice = partSize * markUp;
        item.totalPrice = quantity * item.unitPrice;
      }

      this.calculateGrandTotal();
      this.hasChanges = true;
    },


    updateLabourTotalPrice(item) {
      if (item.jobCategory === "Custom") {
        const quantity = parseFloat(item.quantity) || 0;
        item.totalPrice = quantity * item.unitPrice;
        this.calculateGrandTotal();
        this.hasChanges = true;
      }
    },

    calculateStockTotalPrice(item) {
      if (!item.costPrice) {
        item.totalPrice = Math.round(item.quantity * item.unitPrice);
      } else {
        if (item.jobCategory === "Custom") {
          item.unitPrice = item.costPrice * item.markUp;
        } else {
          item.unitPrice = Math.round(item.costPrice * item.markUp);
        }
        item.totalPrice = Math.round(item.quantity * item.unitPrice);
      }
      this.calculateGrandTotal();
      this.hasChanges = true;
    },

    updateStockTotalPrice(item) {
      if (item.jobCategory === "Custom") {
        const quantity = parseFloat(item.quantity) || 0;
        item.totalPrice = quantity * item.unitPrice;
        this.calculateGrandTotal();
        this.hasChanges = true;
      }
    },

    calculateGrandTotal() {
      this.job.labourSubTotal =
        this.job.labourItems.reduce(
          (acc, item) => acc + parseInt(item.totalPrice || 0),
          0
        ) + (this.job.setRate * this.job.timeToComplete || 0);

      this.job.stockSubTotal = this.job.stockItems.reduce(
        (acc, item) => acc + parseInt(item.totalPrice || 0),
        0
      );

      this.job.grandTotal = this.job.labourSubTotal + this.job.stockSubTotal;
    },

    setMarkup(item) {
      if (item.jobCategory === "Custom") {
        item.markUp = 1;
      } else {
        switch (item.jobCategory) {
          case "Lapping Carbon":
            item.markUp = 4.0;
            break;
          case "Lapping Sic/SSic/SS/Cer":
            item.markUp = 4.5;
            break;
          case "Lapping TC":
            item.markUp = 5.5;
            break;
          case "Bearings":
            item.markUp = 1.1;
            break;
          case "Bolts/Nuts":
            item.markUp = 1.5;
            break;
          case "Coating (Any)":
            item.markUp = 1.1;
            break;
          case "Die Set":
            item.markUp = 1.1;
            break;
          case "Gasket":
            item.markUp = 1.5;
            break;
          case "Grinding - no drawing":
            item.markUp = 1.5;
            break;
          case "Lip Seal / V-seal (not in seal)":
            item.markUp = 1.5;
            break;
          case "Lip Seal / V-seal (in seal or pump repair)":
            item.markUp = 1.1;
            break;
          case "Local Mods":
            item.markUp = 1.5;
            break;
          case "Machining - no drawing":
            item.markUp = 1.5;
            break;
          case "Material":
            item.markUp = 1.0;
            break;
          case "Nexus Drawn item":
            item.markUp = 1.8;
            break;
          case "O-Rings Only Quote":
            item.markUp = 3.0;
            break;
          case "O-Rings with seal":
            item.markUp = 1.5;
            break;
          case "Special Markup":
            item.markUp = 1.5;
            break;
          case "Special New":
            item.markUp = 1.3;
            break;
          case "Stock Item New":
            item.markUp = 1.2;
            break;
          case "Tools":
            item.markUp = 1.1;
            break;
          default:
            item.markUp = 1.1;
            break;
        }
      }
      this.handleItemChange();
    },

    confirmAction(action, index = null) {
      this.confirmedAction = { action, index };
      switch (action) {
        case "removeLabour":
          this.confirmationMessage = "Are you sure you want to remove this labour item?";
          break;
        case "removeStock":
          this.confirmationMessage = "Are you sure you want to remove this stock item?";
          break;
        case "removeCustomField":
          this.confirmationMessage = "Are you sure you want to remove this journal field?";
          break;
        case "addLabour":
          this.confirmationMessage = "Are you sure you want to add a new labour item?";
          break;
        case "addStock":
          this.confirmationMessage = "Are you sure you want to add a new stock item?";
          break;
        case "addCustomField":
          this.confirmationMessage = "Are you sure you want to add a new journal field?";
          break;
        case "saveJobDetails":
          this.confirmationMessage = "Are you sure you want to save the changes?";
          break;
        default:
          this.confirmationMessage = "Are you sure you want to perform this action?";
          break;
      }
      this.confirmationDialog = true;
    },

    async executeConfirmedAction() {
      const { action, index } = this.confirmedAction;
      switch (action) {
        case "removeLabour":
          this.job.labourItems.splice(index, 1);
          this.handleItemChange();
          break;
        case "removeStock":
          this.job.stockItems.splice(index, 1);
          this.handleItemChange();
          break;
        case "removeCustomField":
          this.job.customFields.splice(index, 1);
          this.saveJobDetails();
          break;
        case "addLabour":
          this.job.labourItems.push({
            quantity: 1,
            description: "",
            partSize: 0,
            markUp: 1.0,
            unitPrice: 0,
            totalPrice: 0,
            jobCategory: "",
          });
          this.handleItemChange();
          break;
        case "addStock":
          this.job.stockItems.push({
            quantity: 1,
            code: "",
            drawingNumber: "",
            supplier: "",
            leadTime: "",
            costPrice: 0,
            markUp: 1.0,
            unitPrice: 0,
            totalPrice: 0,
            jobCategory: "",
          });
          this.handleItemChange();
          break;
        case "addCustomField":
          this.dialogAddCustomField = true;
          break;
        case "saveJobDetails":
          await this.saveJobDetails();
          break;
        default:
          console.error("Unknown action:", action);
      }
      this.confirmationDialog = false;
    },

    async addCustomFieldConfirmed() {
      this.dialogAddCustomField = false;
      this.job.customFields.push({ value: "" });
      this.hasChanges = true;
      await this.saveJobDetails();
    },

    async saveJobDetails() {
      const jobId = localStorage.getItem("selectedJob");
      if (jobId) {
        this.loading = true;
        try {
          const jobRef = doc(db, "jobs", jobId);
          const updateData = {
            jobName: this.job.jobName || "",
            customerName: this.job.customerName || "",
            customerNumber: this.job.customerNumber || "",
            urgency: this.job.urgency || "",
            timeToComplete: this.job.timeToComplete || "",
            setRate: this.job.setRate || "",
            currentDate: this.job.currentDate || "",
            dateQuoted: this.job.dateQuoted || "",
            engineer: this.job.engineer || "",
            technician: this.job.technician || "",
            salesperson: this.job.salesperson || "",
            status: this.job.status || "",
            quoteNumber: this.showQuoteNumberField ? this.job.quoteNumber : null,
            TotalLead: this.job.TotalLead || "",
            ShaftSize: this.job.ShaftSize || "",
            salesOrderNumber: this.showSalesOrderField ? this.job.salesOrderNumber : null,
            invoiceNumber: this.showInvoiceNumberField ? this.job.invoiceNumber : null,
            cancellationReason: this.showReasonField ? this.job.cancellationReason : null,
            photos: this.job.photos || [],
            jobDescription: this.job.jobDescription || "",
            JobSummary: this.job.JobSummary || "",
            labourItems: this.job.labourItems || [],
            stockItems: this.job.stockItems || [],
            customFields: this.job.customFields || [],
            labourSubTotal: this.job.labourSubTotal || 0,
            stockSubTotal: this.job.stockSubTotal || 0,
            grandTotal: this.job.grandTotal || 0,
            sampleNumber: this.job.sampleNumber || "",
            customerReference: this.job.customerReference || "",
            inventoryJournalNumber: this.job.inventoryJournalNumber || "",
            extraInfoFields: this.job.extraInfoFields || [],
          };

          await updateDoc(jobRef, updateData);
          console.log("Job details updated successfully");
          this.notify("success", "Job Updated", "The job has been updated successfully.");
          this.originalStatus = this.job.status;
          this.hasChanges = false;
        } catch (error) {
          console.error("Error updating job details: ", error);
          this.notify("danger", "Update Failed", "An error occurred while updating the job.");
        } finally {
          this.loading = false;
        }
      } else {
        console.error("No job ID found");
        this.notify("danger", "Job ID Missing", "Please select a valid job.");
      }
    },

    handleStatusChange() {
      if (this.job.status === "Quoted" && !this.job.dateQuoted) {
        this.job.dateQuoted = new Date().toISOString().split("T")[0];
      }
      this.calculateGrandTotal();
      this.hasChanges = true;
    },

    openChecklistDialog() {
      if (this.job && this.job.jobNumber) {
        this.checklistDialog = true;
      } else {
        console.error("Job number not available");
        this.notify("danger", "Job Number Missing", "No job number available.");
      }
    },

    openBackConfirmation() {
      if (this.hasChanges) {
        this.backDialog = true;
      } else {
        this.goBack();
      }
    },

    goBack() {
      this.backDialog = false;
      this.$router.go(-1);
    },

    notify(color, title, text) {
      if (color === "success") {
        this.successSnack.message = text;
        this.successSnack.show = true;
      } else if (color === "danger") {
        this.errorSnack.message = text;
        this.errorSnack.show = true;
      } else if (color === "info") {
        this.successSnack.message = text;
        this.successSnack.show = true;
      }
    },
  },
  computed: {
    isInspection() {
      return this.job && this.job.status !== "Inspection";
    },
    showQuoteNumberField() {
      return this.job.status === "Quoted";
    },
    showSalesOrderField() {
      return (
        this.job.status === "InProgress" ||
        this.job.status === "Ready" ||
        this.job.status === "Delayed"
      );
    },
    showInvoiceNumberField() {
      return (
        this.job.status === "Completed" ||
        this.job.status === "Costing Complete"
      );
    },
    showReasonField() {
      return this.job.status === "Canceled";
    },
    canChangeStatus() {
      return this.isAdmin;
    },
    statusOptions() {
      return this.statusOrder.map((status, index) => {
        let isDisabled = false;
        if (this.isAdmin) {
          isDisabled = false;
        } else {
          const currentIndex = this.statusOrder.indexOf(this.originalStatus);
          if (status === this.originalStatus) {
            isDisabled = false;
          } else if (status === "Canceled" || status === "Delayed") {
            isDisabled = false;
          } else if (currentIndex !== -1 && index === currentIndex + 1) {
            isDisabled = false;
          } else {
            isDisabled = true;
          }
        }
        return {
          text: status,
          value: status,
          disabled: isDisabled,
        };
      });
    },
    // NEW computed property: if the current user's document (fetched from Firestore)
    // has the permission "View_Line_Items_(Labour_and_Stock)" set to true, hide the labour and stock sections.
    hideLabourStock() {
      return (
        this.currentUser &&
        this.currentUser.jobs &&
        this.currentUser.jobs["View_Line_Items_(Labour_and_Stock)"] === true
      );
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,
div,
h2,
h3,
v-img,
v-text-field,
v-select,
v-autocomplete,
v-textarea,
v-btn {
  font-family: "Montserrat", sans-serif;
  color: #333;
}

.add-job-card {
  background-color: white;
  border-radius: 10px;
  padding: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  margin-top: 22px;
  margin-bottom: 16px;
  color: red;
}

.back-button {
  color: white;
  background-color: red;
  border-radius: 50%;
  position: absolute;
  top: 16px;
  left: 16px;
  transition: transform 0.3s ease-in-out;
}
.back-button:hover {
  transform: scale(1.1);
}

.montserrat-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.montserrat-input {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}

.montserrat-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.loading-photos {
  display: flex;
  align-items: center;
  gap: 10px;
}

.carousel-photo {
  object-fit: contain;
}

.v-btn {
  border-radius: 4px;
}

.v-card {
  border-radius: 8px;
}

.v-dialog__content {
  padding: 0;
}

@media (max-width: 600px) {
  .add-job-card {
    padding: 10px;
  }
  .montserrat-input {
    font-size: 14px;
  }
  .montserrat-button {
    font-size: 14px;
  }
}
</style>
